import type { LogicSubCondition } from '@webapp/common/entities/logic';
import { LogicSubConditonType } from '@webapp/common/entities/logic';
import { createSubdomainUrl } from '@webapp/common/lib/utils';
import type { QuestionLogicModel, SurveyStore } from '@webapp/common/resources/entities';
import { BundleFinishType, BundleType } from '@webapp/common/resources/entities';
import {
    checkIsQWithClickReply,
    LinkParamsLogicType,
    LogicAction,
    LogicBoolFunc,
    LogicTransition,
    LogicTransitionType,
    LogicTransitionVariant,
    LogicType,
    QuestionType,
} from '@webapp/common/resources/survey';

import type { QuestionAnswerActor, QuestionAnswerContext } from '@webapp/survey/src/entities/answer-machine';
import { BundleEntity } from '@webapp/survey/src/entities/bundle';
import type { QuestionResponseContext } from '@webapp/survey/src/entities/response-machine';
import type { GroupContext } from '@webapp/survey/src/machine/group-machine';
import { getQ } from '@webapp/survey/src/machine/lib';
import type { QuestionActorRef, QuestionContext } from '@webapp/survey/src/machine/question-machine';

export const getGroupsValues = (groups: Array<GroupContext>): Array<string | number> =>
    groups.reduce((acc, { responses }) => {
        const entries = Object.keys(responses)
            .map((rKey) => responses[rKey])
            .map((r) => r.getSnapshot().context.value);

        if (entries.length > 0) {
            acc.push(entries);
        }

        return acc;
    }, []);

const TEXT_FINISH = new Set([
    LogicTransition.PERSONAL_COMPLETE,
    LogicTransition.DISQUAL,
    LogicTransition.PAGES,
    LogicTransition.QUESTIONS,
]);

export const createLastBundle = ({
    completeText,
    disqualText,
    toSurveyUrl,
    toWebsite,
    transition: transitionRaw,
    transitionType,
    finishTransition,
    copyUrlParams,
}: any): BundleEntity => {
    const transition = transitionRaw === LogicTransition.FINISH_SURVEY ? finishTransition : transitionRaw;
    const lastBundle = new BundleEntity(
        {
            random: false,
            questions: [],
            finishType: (() => {
                switch (transition) {
                    case LogicTransition.PERSONAL_COMPLETE:
                        return BundleFinishType.STOP;
                    case LogicTransition.DISQUAL:
                        return BundleFinishType.DISQUAL;
                    default:
                        return BundleFinishType.STOP; // TODO FIXME not used
                }
            })(),
        },
        [],
    );

    if (!TEXT_FINISH.has(transition)) return lastBundle;

    if (transition === LogicTransition.PAGES || transition === LogicTransition.QUESTIONS) {
        lastBundle.type = BundleType.TEXT;
        if (transitionType === LogicTransitionType.TEXT) {
            lastBundle.text = completeText;
        }
        return lastBundle;
    }

    switch (transitionType) {
        case LogicTransitionType.TEXT:
            lastBundle.type = BundleType.TEXT;
            lastBundle.text = LogicTransition.PERSONAL_COMPLETE === transition ? completeText : disqualText;
            break;
        case LogicTransitionType.TO_SURVEY:
            lastBundle.type = BundleType.REDIRECT_SURVEY;
            if (!toSurveyUrl) {
                console.error(`No survey url for transition toSurveyUrl ${transition}`);
            }
            lastBundle.redirect = createSubdomainUrl(toSurveyUrl);
            break;
        case LogicTransitionType.REDIRECT_TO_WEBSITE:
            lastBundle.type = BundleType.REDIRECT_URL;
            if (!toWebsite) {
                console.error(`No survey url for transition toWebsite ${transition}`);
            }
            lastBundle.redirect = toWebsite;
            break;
    }
    lastBundle.copyUrlParams = Boolean(copyUrlParams);
    return lastBundle;
};

const logicMatch = (
    logic: Pick<QuestionLogicModel, 'boolFunc' | 'params' | 'type' | 'id'>,
    groups: Array<GroupContext>,
    answers: Array<QuestionAnswerContext>,
    response: QuestionResponseContext,
    questionCtx: QuestionContext,
    quotaByQuestionId: Record<number, boolean>,
): boolean => {
    const { boolFunc, params, type } = logic;
    const questionType = questionCtx.type;
    const notScale = questionType !== QuestionType.SCALE; // TODO fix backend, dont use groups
    const withGroups = Boolean(notScale && groups && groups.length > 0);
    const withAnswers = Boolean(notScale && answers && answers.length > 0);
    const variantsIds: Record<number, boolean> = params.reduce((acc, { id }) => {
        acc[id] = true;
        return acc;
    }, {}); // TODO use Set
    const answersById: Record<string, QuestionAnswerContext> = answers.reduce((acc, a) => {
        acc[a.id] = a;
        return acc;
    }, {});
    let satisfied = false;

    switch (type) {
        case LogicType.TYPE_QUESTION_QUOTA: {
            let isSomeOfAnswersSelected = false;
            if (questionType === QuestionType.MATRIX_RATING) {
                isSomeOfAnswersSelected = Object.keys(variantsIds).some((id) => {
                    const [, rating, answerId] = id.toString().split('-');
                    return answersById[answerId]?.response.getSnapshot().context.value?.toString() === rating;
                });
            } else if (
                questionType === QuestionType.MATRIX_SINGLE_ANSWER ||
                questionType === QuestionType.MATRIX_FEW_ANSWERS
            ) {
                isSomeOfAnswersSelected = Object.keys(variantsIds).some((id) => {
                    const [, groupId, answerId] = id.toString().split('-');
                    return !!groups.find((gr) => gr.id.toString() === groupId)?.responses[answerId]?.getSnapshot()
                        .context.value;
                });
            } else if (withGroups) {
                const values = getGroupsValues(groups);
                isSomeOfAnswersSelected = values.some((answerId) => variantsIds[answerId]);
            } else if (withAnswers) {
                isSomeOfAnswersSelected = answers.some(
                    ({ id, response }) => !!response.getSnapshot().context.value && variantsIds[id],
                );
            } else {
                isSomeOfAnswersSelected = !!variantsIds[response.value as number];
            }
            satisfied = quotaByQuestionId[logic.id] === false && isSomeOfAnswersSelected;
            break;
        }
        case LogicType.SELECT_VARIANTS:
            switch (boolFunc) {
                case LogicBoolFunc.AND:
                    if (questionType === QuestionType.MATRIX_RATING) {
                        const groupedByRowId: { [key: string]: Array<{ rowId: string; rating: string }> } = Object.keys(
                            variantsIds,
                        ).reduce((acc, id) => {
                            const [, rating, rowId] = id.toString().split('-');
                            if (!acc[rowId]) {
                                acc[rowId] = [{ rowId, rating }];
                            } else {
                                acc[rowId].push({ rowId, rating });
                            }
                            return acc;
                        }, {} as { [key: string]: Array<{ rowId: string; rating: string }> });
                        satisfied =
                            Object.keys(groupedByRowId).length > 0 &&
                            Object.keys(groupedByRowId).every((rowId) => {
                                const rowAnswers = groupedByRowId[rowId];
                                return rowAnswers.some(
                                    (rowAns) =>
                                        answersById[rowId]?.response?.getSnapshot().context.value?.toString() ===
                                        rowAns.rating,
                                );
                            });
                    } else if (
                        questionType === QuestionType.MATRIX_SINGLE_ANSWER ||
                        questionType === QuestionType.MATRIX_FEW_ANSWERS
                    ) {
                        const groupedByRowId: { [key: string]: Array<{ rowId: string; groupId: string }> } =
                            Object.keys(variantsIds).reduce((acc, id) => {
                                const [, groupId, rowId] = id.toString().split('-');
                                if (!acc[rowId]) {
                                    acc[rowId] = [{ rowId, groupId }];
                                } else {
                                    acc[rowId].push({ rowId, groupId });
                                }
                                return acc;
                            }, {} as { [key: string]: Array<{ rowId: string; groupId: string }> });
                        satisfied =
                            Object.keys(groupedByRowId).length > 0 &&
                            Object.keys(groupedByRowId).every((rowId) => {
                                const rowAnswers = groupedByRowId[rowId];
                                return rowAnswers.some(
                                    (rowAns) =>
                                        !!groups
                                            .find((gr) => gr.id.toString() === rowAns.groupId)
                                            ?.responses[rowAns.rowId]?.getSnapshot().context.value,
                                );
                            });
                    } else if (withGroups) {
                        // const values = getGroupsValues(groups);
                        // satisfied = values.some((answerId) => variantsIds[answerId]);
                    } else if (withAnswers) {
                        satisfied =
                            Object.keys(variantsIds).length > 0 &&
                            Object.keys(variantsIds).every(
                                (id) => !!answersById[id]?.response.getSnapshot().context.value,
                            );
                    }
                    break;
                case LogicBoolFunc.OR:
                    if (questionType === QuestionType.MATRIX_RATING) {
                        satisfied = Object.keys(variantsIds).some((id) => {
                            const [, rating, answerId] = id.toString().split('-');
                            return answersById[answerId]?.response.getSnapshot().context.value?.toString() === rating;
                        });
                    } else if (
                        questionType === QuestionType.MATRIX_SINGLE_ANSWER ||
                        questionType === QuestionType.MATRIX_FEW_ANSWERS
                    ) {
                        satisfied = Object.keys(variantsIds).some((id) => {
                            const [, groupId, answerId] = id.toString().split('-');
                            return !!groups
                                .find((gr) => gr.id.toString() === groupId)
                                ?.responses[answerId]?.getSnapshot().context.value;
                        });
                    } else if (withGroups) {
                        const values = getGroupsValues(groups);
                        satisfied = values.some((answerId) => variantsIds[answerId]);
                    } else if (withAnswers) {
                        satisfied = answers.some(
                            ({ id, response }) => !!response.getSnapshot().context.value && variantsIds[id],
                        );
                    } else {
                        satisfied = !!variantsIds[response.value as number];
                    }
                    break;
            }

            break;
        case LogicType.NOT_SELECT_VARIANTS:
            switch (boolFunc) {
                case LogicBoolFunc.AND:
                    if (withGroups) {
                        // const values = getGroupsValues(groups);
                        // satisfied = !values.every((answerId) => variantsIds[answerId]);
                    } else if (withAnswers) {
                        satisfied =
                            Object.keys(variantsIds).length > 0 &&
                            Object.keys(variantsIds).every(
                                (id) => !answersById[id]?.response?.getSnapshot().context.value,
                            );
                    } else {
                    }

                    break;
                case LogicBoolFunc.OR:
                    if (withGroups) {
                        const values = getGroupsValues(groups);
                        satisfied = !values.every((answerId) => variantsIds[answerId]);
                    } else if (withAnswers) {
                        if (checkIsQWithClickReply(questionCtx)) {
                            const filtered = answers.filter(({ id }) => variantsIds[id]);
                            satisfied =
                                filtered.length > 0 &&
                                filtered.every(({ response }) => !response.getSnapshot().context.value);
                        } else {
                            satisfied = answers.some(
                                ({ id, response }) => variantsIds[id] && !response.getSnapshot().context.value,
                            );
                        }
                    } else {
                        satisfied = !variantsIds[response.value as number];
                    }

                    break;
            }

            break;
        case LogicType.SKIP:
            if (!response.value) {
                satisfied = true;
            }

            if (withAnswers) {
                satisfied =
                    answers.length > 0 && answers.every(({ response }) => !response.getSnapshot().context.value);
            }

            if (withGroups) {
                satisfied =
                    groups.length > 0 &&
                    groups.every(({ responses }) => {
                        let empty = true;

                        for (const key of Object.keys(responses)) {
                            const response = responses[key].getSnapshot().context;
                            if (response.value) {
                                empty = false;
                                break;
                            }
                        }

                        return empty;
                    });
            }

            break;
        case LogicType.ALWAYS:
            satisfied = true;

            break;
    }

    return satisfied;
};

export const adjustQuestions = (bundles: Array<BundleEntity>, shownByLogic: boolean): void => {
    let questionOrder = 0;
    bundles.forEach(({ questions }) => {
        questions.forEach((q) => {
            q.send({ type: 'SET_SHOW_BY_LOGIC', value: shownByLogic });
            if (q.getSnapshot().context.type !== QuestionType.TEXT_BLOCK) {
                q.send({ type: 'SET_ORDER', value: ++questionOrder });
            }
        });
    });
};

const checkIsAllQuestionsHideByCommonLogic = (
    bundles: Array<BundleEntity>,
    logics: Array<QuestionLogicModel>,
    extendedLogics: Array<QuestionLogicModel>,
): boolean => {
    const commonQuestionsLogicMatches: Array<QuestionLogicModel> = logics
        .filter(
            (logic) =>
                logic.type === LogicType.LINK_PARAM &&
                logic.transition === LogicTransition.QUESTIONS &&
                logic.action === LogicAction.SHOW,
        )
        .concat(
            extendedLogics.filter(
                (el) => el.transition === LogicTransition.QUESTIONS && el.action === LogicAction.SHOW,
            ),
        );
    const commonPagesLogicMatches: Array<QuestionLogicModel> = logics
        .filter(
            (logic) =>
                logic.type === LogicType.LINK_PARAM &&
                logic.transition === LogicTransition.PAGES &&
                logic.action === LogicAction.SHOW,
        )
        .concat(
            extendedLogics.filter((el) => el.transition === LogicTransition.PAGES && el.action === LogicAction.SHOW),
        );
    const commonHideQuestionsLogicMatches: Array<QuestionLogicModel> = logics
        .filter(
            (logic) =>
                logic.type === LogicType.LINK_PARAM &&
                logic.transition === LogicTransition.QUESTIONS &&
                logic.action === LogicAction.HIDE,
        )
        .concat(
            extendedLogics.filter(
                (el) => el.transition === LogicTransition.QUESTIONS && el.action === LogicAction.HIDE,
            ),
        );
    const commonHidePagesLogicMatches: Array<QuestionLogicModel> = logics
        .filter(
            (logic) =>
                logic.type === LogicType.LINK_PARAM &&
                logic.transition === LogicTransition.PAGES &&
                logic.action === LogicAction.HIDE,
        )
        .concat(
            extendedLogics.filter((el) => el.transition === LogicTransition.PAGES && el.action === LogicAction.HIDE),
        );
    let pushedQuestions = 0;
    bundles.forEach((bundle) => {
        bundle.questions.forEach((questionActor) => {
            const question = questionActor.getSnapshot().context;
            if (
                commonHidePagesLogicMatches.length > 0 &&
                commonHidePagesLogicMatches.some((l) => l.toPages.includes(question.page))
            ) {
                return;
            }
            if (
                commonHideQuestionsLogicMatches.length > 0 &&
                commonHideQuestionsLogicMatches.some((l) => l.toQuestions.includes(question.id))
            ) {
                return;
            }
            if (
                commonPagesLogicMatches.length > 0 &&
                !commonPagesLogicMatches.some((l) => l.toPages.includes(question.page))
            ) {
                return;
            }
            if (
                commonQuestionsLogicMatches.length > 0 &&
                !commonQuestionsLogicMatches.some((l) => l.toQuestions.includes(question.id))
            ) {
                return;
            }
            pushedQuestions++;
        });
    });
    return pushedQuestions === 0;
};
export const filterBundlesQuestionsByLogic = (
    bundles: Array<BundleEntity>,
    globalLogic: Array<QuestionLogicModel>,
    queryParamsRaw: Record<string, string>,
    linkParamsRaw: Record<string, string>,
    extendedLogic: Array<QuestionLogicModel>,
): { newBundles: Array<BundleEntity>; hiddenAnswers: Record<number, Array<number>> } => {
    const hiddenAnswers: Record<number, Array<number>> = {};
    const queryParams = queryParamsRaw || {};
    const linkParams = linkParamsRaw || {};

    const reversedQpKey = Object.keys(linkParams).reduce((acc, item) => {
        acc[linkParams[item]] = item;
        return acc;
    }, {} as Record<string, string>);

    const matchedLinkParams = Object.keys(queryParams).reduce((acc, qpKey) => {
        if (reversedQpKey[qpKey]) {
            acc[reversedQpKey[qpKey]] = queryParams[qpKey];
        }
        return acc;
    }, {} as Record<string, string>);

    const matchedGlobalLogicRaw = globalLogic.filter((logic) =>
        logic.linkParamsLogicType === LinkParamsLogicType.NOT_EQUAL
            ? matchedLinkParams[logic.linkId]?.toLowerCase() !== logic.linkValue?.toLowerCase()
            : matchedLinkParams[logic.linkId]?.toLowerCase() === logic.linkValue?.toLowerCase(),
    );

    let matchedGlobalLogic = matchedGlobalLogicRaw.filter((logic) => logic.action !== LogicAction.ANSWERS_HIDE);
    const matchedGlobalLogicAnswersHide = matchedGlobalLogicRaw.filter(
        (logic) => logic.action === LogicAction.ANSWERS_HIDE,
    );

    const matchedExtendedLogicRaw = extendedLogic
        .map((logic) => {
            const conditionsWithMatchedFlag: Array<{
                cond: LogicSubCondition;
                isMatched: boolean;
                boolFuncExternal: LogicBoolFunc;
            }> = logic.conditions.map((cond) => {
                if (cond.type === LogicSubConditonType.LINK_PARAM) {
                    return {
                        cond,
                        isMatched:
                            cond.respondent === LogicType.SELECT_VARIANTS
                                ? matchedLinkParams[cond.linkParamId]
                                      ?.toLowerCase()
                                      ?.includes(cond.linkParamValue?.toLowerCase())
                                : !matchedLinkParams[cond.linkParamId]
                                      ?.toLowerCase()
                                      ?.includes(cond.linkParamValue?.toLowerCase()),
                        boolFuncExternal: cond.boolFuncExternal,
                    };
                }
                let question: QuestionActorRef | null = null;

                bundles.forEach((bundle) =>
                    bundle.questions.forEach((questionActor) => {
                        if (questionActor.getSnapshot().context.id === cond.questionId) {
                            question = questionActor;
                        }
                    }),
                );

                if (question) {
                    const qContext = question.getSnapshot().context;
                    const { groupsActors, answersActors, responseActor } = qContext;

                    const isMatched = logicMatch(
                        {
                            id: cond.id,
                            boolFunc: cond.boolFunc,
                            type: cond.respondent,
                            params: cond.variants
                                .filter((v) => v.checked)
                                .map((v) => ({ id: v.id as number, checked: v.checked })),
                        },
                        groupsActors.map((g) => g.getSnapshot().context),
                        answersActors.map((a) => a.getSnapshot().context),
                        responseActor.getSnapshot().context,
                        qContext,
                        {},
                    );

                    return {
                        cond,
                        isMatched,
                        boolFuncExternal: cond.boolFuncExternal,
                    };
                }
                return {
                    cond,
                    isMatched: false,
                    boolFuncExternal: cond.boolFuncExternal,
                };
            });
            const matchedConditions = conditionsWithMatchedFlag
                .filter((cond) => cond.isMatched)
                .map(({ cond }) => cond);
            const conditionsWithMatchedFlagWithCollapsedAnd: Array<{
                isMatched: boolean;
                boolFuncExternal: LogicBoolFunc;
            }> = [];
            let isOpenBracket = false;
            let addConds: Array<{
                isMatched: boolean;
                boolFuncExternal: LogicBoolFunc;
            }> = [];
            conditionsWithMatchedFlag.forEach((cond) => {
                if (cond.boolFuncExternal === LogicBoolFunc.AND) {
                    isOpenBracket = true;
                    addConds.push(cond);
                } else {
                    if (isOpenBracket) {
                        addConds.push(cond);
                        conditionsWithMatchedFlagWithCollapsedAnd.push({
                            boolFuncExternal: cond.boolFuncExternal,
                            isMatched: addConds.every((c) => c.isMatched),
                        });
                        isOpenBracket = false;
                        addConds = [];
                    } else {
                        conditionsWithMatchedFlagWithCollapsedAnd.push({
                            isMatched: cond.isMatched,
                            boolFuncExternal: cond.boolFuncExternal,
                        });
                    }
                }
            });
            if (isOpenBracket) {
                conditionsWithMatchedFlagWithCollapsedAnd.push({
                    boolFuncExternal: LogicBoolFunc.OR,
                    isMatched: addConds.every((c) => c.isMatched),
                });
                isOpenBracket = false;
                addConds = [];
            }
            return {
                isMatched: conditionsWithMatchedFlagWithCollapsedAnd.some((c) => c.isMatched),
                logic: { ...logic, conditions: matchedConditions },
            };
        })
        .filter((logic) => logic.isMatched)
        .map(({ logic }) => logic);
    let matchedExtendedLogic = matchedExtendedLogicRaw.filter((logic) => logic.action !== LogicAction.ANSWERS_HIDE);
    const matchedExtendedLogicAnswersHide = matchedExtendedLogicRaw.filter(
        (logic) => logic.action === LogicAction.ANSWERS_HIDE,
    );
    // TODO: скрывать по extendedLogic
    const isAllQuestionsHideByCommonLogic = checkIsAllQuestionsHideByCommonLogic(
        bundles,
        matchedGlobalLogic,
        matchedExtendedLogic,
    );
    if (isAllQuestionsHideByCommonLogic) {
        matchedGlobalLogic = [];
        matchedExtendedLogic = matchedExtendedLogic.filter((el) => {
            if (el.transition === LogicTransition.QUESTIONS || el.transition === LogicTransition.PAGES) {
                return false;
            }
            return true;
        });
    }

    let execLogic: QuestionLogicModel;
    const questionsLogicMatches: Array<QuestionLogicModel> = [];
    const pagesLogicMatches: Array<QuestionLogicModel> = [];
    const hideQuestionsLogicMatches: Array<QuestionLogicModel> = [];
    const hidePagesLogicMatches: Array<QuestionLogicModel> = [];
    const commonQuestionsLogicMatches: Array<QuestionLogicModel> = matchedGlobalLogic.filter(
        (logic) =>
            logic.type === LogicType.LINK_PARAM &&
            logic.transition === LogicTransition.QUESTIONS &&
            logic.action === LogicAction.SHOW,
    );
    const commonPagesLogicMatches: Array<QuestionLogicModel> = matchedGlobalLogic.filter(
        (logic) =>
            logic.type === LogicType.LINK_PARAM &&
            logic.transition === LogicTransition.PAGES &&
            logic.action === LogicAction.SHOW,
    );
    const commonHideQuestionsLogicMatches: Array<QuestionLogicModel> = matchedGlobalLogic.filter(
        (logic) =>
            logic.type === LogicType.LINK_PARAM &&
            logic.transition === LogicTransition.QUESTIONS &&
            logic.action === LogicAction.HIDE,
    );
    const commonHidePagesLogicMatches: Array<QuestionLogicModel> = matchedGlobalLogic.filter(
        (logic) =>
            logic.type === LogicType.LINK_PARAM &&
            logic.transition === LogicTransition.PAGES &&
            logic.action === LogicAction.HIDE,
    );

    matchedGlobalLogic = matchedGlobalLogic.filter((l) => l.type !== LogicType.LINK_PARAM);
    adjustQuestions(bundles, false);
    if (!bundles?.[0]?.questions?.[0]) return { newBundles: [], hiddenAnswers: {} };
    const root = bundles[0].questions[0]._parent.getSnapshot().context as SurveyStore;
    const quotaByQuestionId = root.quotaRef?.getSnapshot().context.quotaStatusByQuestionId || {};

    const newBundles = bundles.reduce((acc, bundle, bundleIndex) => {
        const matchedExtendedLogicWithBundleIndex = matchedExtendedLogic.filter((logic) => {
            const logicQuestionIds = logic.conditions
                .filter((c) => c.type === LogicSubConditonType.QUESTION)
                .map((cond) => cond.questionId);
            const bundlesIdxs = logicQuestionIds.map((questionId) =>
                bundles.findIndex((b) => b.questions.find((q) => q.getSnapshot().context.id === questionId)),
            );
            const maxIdx = Math.max(0, Math.max(...bundlesIdxs));
            return logicQuestionIds.length === 0 || maxIdx < bundleIndex;
        });
        if (matchedExtendedLogicWithBundleIndex.length > 0) {
            for (const extendedLogic of matchedExtendedLogicWithBundleIndex) {
                const logicQuestionIds = extendedLogic.conditions
                    .filter((c) => c.type === LogicSubConditonType.QUESTION)
                    .map((cond) => cond.questionId);
                const bundlesIdxs = logicQuestionIds.map((questionId) =>
                    bundles.findIndex((b) => b.questions.find((q) => q.getSnapshot().context.id === questionId)),
                );
                const maxIdx = Math.max(0, Math.max(...bundlesIdxs));
                if (maxIdx === -1 || maxIdx < bundleIndex || logicQuestionIds.length === 0) {
                    switch (extendedLogic.transition) {
                        case LogicTransition.PAGE: {
                            const toPageIndex = bundles.findIndex((bundle) => bundle.page.id === extendedLogic.toPage);

                            if (toPageIndex < bundleIndex) {
                                continue;
                            }
                            if (extendedLogic.toPage !== bundle.page.id) {
                                return acc;
                            }
                            break;
                        }
                        case LogicTransition.PAGES: {
                            if (extendedLogic.action === LogicAction.HIDE) {
                                if (extendedLogic.toPages.some((toPage) => toPage === bundle.page.id)) {
                                    return acc;
                                } else {
                                    continue;
                                }
                            }
                            const toPageIndexes = extendedLogic.toPages.map((toPage) =>
                                bundles.findIndex((bundle) => bundle.page.id === toPage),
                            );

                            if (toPageIndexes.some((toPageIndex) => toPageIndex < bundleIndex)) {
                                continue;
                            }
                            if (extendedLogic.toPage !== bundle.page.id) {
                                return acc;
                            }
                            break;
                        }
                        case LogicTransition.FINISH_SURVEY: {
                            if (extendedLogic.finishTransitionVariant === LogicTransitionVariant.AFTER_QUESTION) {
                                const afterQuestionBundleIndex = bundles.findIndex((bundle) =>
                                    bundle.questions.find(
                                        (q) => q.getSnapshot().context.id === extendedLogic.afterQuestionId,
                                    ),
                                );
                                if (afterQuestionBundleIndex < bundleIndex) {
                                    switch (extendedLogic.finishTransition) {
                                        case LogicTransition.DISQUAL:
                                        case LogicTransition.PERSONAL_COMPLETE:
                                        case LogicTransition.REGULAR_COMPLETE:
                                            execLogic = {
                                                ...extendedLogic,
                                                transition: extendedLogic.finishTransition,
                                            };
                                            return acc;
                                    }
                                } else {
                                    break;
                                }
                            } else {
                                switch (extendedLogic.finishTransition) {
                                    case LogicTransition.DISQUAL:
                                    case LogicTransition.PERSONAL_COMPLETE:
                                    case LogicTransition.REGULAR_COMPLETE:
                                        execLogic = {
                                            ...extendedLogic,
                                            transition: extendedLogic.finishTransition,
                                        };
                                        return acc;
                                }
                            }
                        }
                    }
                }
            }
        }
        switch (execLogic?.transition) {
            case LogicTransition.PAGE:
                if (execLogic.toPage !== bundle.page.id) {
                    return acc;
                }
                execLogic = null;
                break;
            case LogicTransition.DISQUAL:
            case LogicTransition.PERSONAL_COMPLETE:
            case LogicTransition.REGULAR_COMPLETE:
                return acc;
        }
        bundle.questions = bundle.questions.reduce((questions, question, qIdx) => {
            if (
                Array.isArray(hiddenAnswers[question.getSnapshot().context.id]) &&
                hiddenAnswers[question.getSnapshot().context.id].length > 0 &&
                question
                    .getSnapshot()
                    .context.answersActors.every((actor) =>
                        hiddenAnswers[question.getSnapshot().context.id].includes(actor.getSnapshot().context.id),
                    )
            ) {
                return questions;
            }
            const matchedExtendedLogicWithBundleAndQuestionIndex = matchedExtendedLogic.filter((logic) => {
                const logicQuestionIds = logic.conditions
                    .filter((c) => c.type === LogicSubConditonType.QUESTION)
                    .map((cond) => cond.questionId);
                const bundlesIdxs = logicQuestionIds.map((questionId) =>
                    bundles.findIndex((b) => b.questions.find((q) => q.getSnapshot().context.id === questionId)),
                );
                const maxIdx = Math.max(...bundlesIdxs);
                const bundleQuestionsFromCondition = bundle.questions.filter((q) =>
                    logicQuestionIds?.includes(q.getSnapshot().context.id),
                );
                const maxIdxQuestion = bundleQuestionsFromCondition[bundleQuestionsFromCondition.length - 1];
                const maxIdxQuestionId = maxIdxQuestion?.getSnapshot().context.id;
                const maxIdxQuestionIdIndexInBundle = bundle.questions.findIndex(
                    (q) => q.getSnapshot().context.id === maxIdxQuestionId,
                );

                if (maxIdx === -1 || logicQuestionIds.length === 0) return true;
                if (maxIdx > bundleIndex) {
                    return false;
                }
                if (bundleIndex > maxIdx) {
                    return true;
                }
                return bundleIndex === maxIdx && qIdx >= maxIdxQuestionIdIndexInBundle;
            });
            if (matchedExtendedLogicWithBundleAndQuestionIndex.length > 0) {
                for (const extendedLogic of matchedExtendedLogicWithBundleAndQuestionIndex) {
                    const logicQuestionIds = extendedLogic.conditions
                        .filter((c) => c.type === LogicSubConditonType.QUESTION)
                        .map((cond) => cond.questionId);
                    const bundlesIdxs = logicQuestionIds.map((questionId) =>
                        bundles.findIndex((b) => b.questions.find((q) => q.getSnapshot().context.id === questionId)),
                    );
                    const maxIdx = Math.max(...bundlesIdxs);
                    const bundleQuestionsFromCondition = bundle.questions.filter((q) =>
                        logicQuestionIds?.includes(q.getSnapshot().context.id),
                    );
                    const maxIdxQuestion = bundleQuestionsFromCondition[bundleQuestionsFromCondition.length - 1];
                    const maxIdxQuestionId = maxIdxQuestion?.getSnapshot().context.id;
                    const maxIdxQuestionIdIndexInBundle = bundle.questions.findIndex(
                        (q) => q.getSnapshot().context.id === maxIdxQuestionId,
                    );
                    if (maxIdx === -1 || maxIdx <= bundleIndex) {
                        switch (extendedLogic.transition) {
                            case LogicTransition.FINISH_SURVEY: {
                                if (extendedLogic.finishTransitionVariant === LogicTransitionVariant.AFTER_QUESTION) {
                                    const afterQuestionBundleIndex = bundles.findIndex((bundle) =>
                                        bundle.questions.find(
                                            (q) => q.getSnapshot().context.id === extendedLogic.afterQuestionId,
                                        ),
                                    );
                                    if (afterQuestionBundleIndex === bundleIndex) {
                                        const bundle = bundles[afterQuestionBundleIndex];
                                        const afterQuestionIndexInBundle = bundle.questions.findIndex(
                                            (q) => q.getSnapshot().context.id === extendedLogic.afterQuestionId,
                                        );
                                        if (afterQuestionIndexInBundle === qIdx) {
                                            execLogic = {
                                                ...extendedLogic,
                                                transition: extendedLogic.finishTransition,
                                            };
                                            questions.push(question);
                                            return questions;
                                        }
                                        if (afterQuestionIndexInBundle < qIdx) {
                                            execLogic = {
                                                ...extendedLogic,
                                                transition: extendedLogic.finishTransition,
                                            };
                                            return questions;
                                        }
                                    }
                                } else if (
                                    maxIdxQuestionIdIndexInBundle !== -1 &&
                                    maxIdxQuestionIdIndexInBundle < qIdx
                                ) {
                                    execLogic = { ...extendedLogic, transition: extendedLogic.finishTransition };
                                    return questions;
                                }
                                break;
                            }
                            case LogicTransition.PAGE:
                                const toPageIndex = bundles.findIndex(
                                    (bundle) => bundle.page.id === extendedLogic.toPage,
                                );
                                if (
                                    extendedLogic.toPage !== bundle.page.id &&
                                    toPageIndex < bundleIndex &&
                                    maxIdx < bundleIndex
                                ) {
                                    return questions;
                                }

                                if (maxIdx === bundleIndex && qIdx > maxIdxQuestionIdIndexInBundle) {
                                    return questions;
                                }
                                break;
                            case LogicTransition.QUESTION: {
                                const questionBundleIndex = bundles.findIndex((b) =>
                                    b.questions.find((q) => q.getSnapshot().context.id === extendedLogic.toQuestion),
                                );
                                if (questionBundleIndex < bundleIndex) {
                                    continue;
                                }
                                if (questionBundleIndex > bundleIndex) {
                                    if (maxIdx !== bundleIndex) {
                                        return questions;
                                    }
                                    if (
                                        bundle.questions.findIndex(
                                            (q) => q.getSnapshot().context.id === maxIdxQuestionId,
                                        ) < qIdx
                                    ) {
                                        return questions;
                                    }
                                    break;
                                }
                                if (questionBundleIndex === bundleIndex) {
                                    const questionIndexInBundle = bundle.questions.findIndex(
                                        (q) => q.getSnapshot().context.id === extendedLogic.toQuestion,
                                    );
                                    if (questionIndexInBundle < qIdx) {
                                        break;
                                    }
                                    if (extendedLogic.toQuestion !== question.getSnapshot().context.id) {
                                        return questions;
                                    }
                                }
                                break;
                            }
                            case LogicTransition.QUESTIONS: {
                                if (extendedLogic.action === LogicAction.HIDE) {
                                    if (extendedLogic.toQuestions?.includes(question.getSnapshot().context.id)) {
                                        return questions;
                                    } else {
                                        continue;
                                    }
                                }
                                if (maxIdx === bundleIndex && qIdx <= maxIdxQuestionIdIndexInBundle) {
                                    continue;
                                }

                                if (!extendedLogic.toQuestions?.includes(question.getSnapshot().context.id)) {
                                    return questions;
                                }

                                break;
                            }
                        }
                    }
                }
            }
            const { answersActors, groupsActors, id, logics, responseActor, page } = getQ(question);
            if (
                commonPagesLogicMatches.length > 0 &&
                commonPagesLogicMatches.every((logic) => !logic.toPages.includes(page))
            ) {
                return questions;
            }
            if (
                commonHidePagesLogicMatches.length > 0 &&
                commonHidePagesLogicMatches.some((logic) => logic.toPages.includes(page))
            ) {
                return questions;
            }
            if (
                commonQuestionsLogicMatches.length > 0 &&
                commonQuestionsLogicMatches.every((logic) => !logic.toQuestions.includes(id))
            ) {
                return questions;
            }
            if (
                commonHideQuestionsLogicMatches.length > 0 &&
                commonHideQuestionsLogicMatches.some((logic) => logic.toQuestions.includes(id))
            ) {
                return questions;
            }
            if (
                pagesLogicMatches.length > 0 &&
                pagesLogicMatches.some((pagesLogic) => !pagesLogic.toPages.includes(bundle.page.id))
            ) {
                return questions;
            }
            if (
                questionsLogicMatches.length > 0 &&
                questionsLogicMatches.some((questionsLogic) => !questionsLogic.toQuestions.includes(id))
            ) {
                return questions;
            }
            if (
                hidePagesLogicMatches.length > 0 &&
                hidePagesLogicMatches.some((pagesLogic) => pagesLogic.toPages.includes(bundle.page.id))
            ) {
                return questions;
            }
            if (
                hideQuestionsLogicMatches.length > 0 &&
                hideQuestionsLogicMatches.some((questionsLogic) => questionsLogic.toQuestions.includes(id))
            ) {
                return questions;
            }
            if (execLogic) {
                switch (execLogic.transition) {
                    case LogicTransition.DISQUAL:
                    case LogicTransition.PERSONAL_COMPLETE:
                    case LogicTransition.REGULAR_COMPLETE:
                        return questions;
                    case LogicTransition.PAGE:
                        if (execLogic.toPage !== bundle.page.id) {
                            return questions;
                        }

                        execLogic = null;

                        break;
                    case LogicTransition.QUESTION:
                        if (execLogic.toQuestion !== id) {
                            return questions;
                        }

                        execLogic = null;

                        break;
                    case LogicTransition.QUESTIONS:
                        questionsLogicMatches.push(execLogic);
                        if (!execLogic.toQuestions.includes(id)) {
                            return questions;
                        }

                        execLogic = null;

                        break;
                    case LogicTransition.PAGES:
                        pagesLogicMatches.push(execLogic);
                        if (!execLogic.toPages.includes(bundle.page.id)) {
                            return questions;
                        }

                        execLogic = null;

                        break;
                    case LogicTransition.ANSWERS_HIDE: {
                        break;
                    }
                }
            }

            if (!bundle.random) {
                const sortedLogicsRaw = logics.slice().sort((a, b) => {
                    const isAOneOf = [LogicTransition.QUESTIONS, LogicTransition.PAGES].includes(a.transition);
                    const isBOneOf = [LogicTransition.QUESTIONS, LogicTransition.PAGES].includes(b.transition);
                    if (isAOneOf && isBOneOf) {
                        return 0;
                    }
                    if (isAOneOf) {
                        return 1;
                    }
                    if (isBOneOf) {
                        return -1;
                    }
                    return 0;
                });
                const sortedLogics = sortedLogicsRaw.filter((l) => l.action !== LogicAction.ANSWERS_HIDE);
                const matchedHideAnswersLogics = matchedGlobalLogicAnswersHide
                    .concat(matchedExtendedLogicAnswersHide)
                    .concat(
                        sortedLogicsRaw
                            .filter((l) => l.action === LogicAction.ANSWERS_HIDE)
                            .filter((logic) =>
                                logicMatch(
                                    logic,
                                    groupsActors.map((g) => g.getSnapshot().context),
                                    answersActors.map((a) => a.getSnapshot().context),
                                    responseActor.getSnapshot().context,
                                    getQ(question),
                                    quotaByQuestionId,
                                ),
                            ),
                    );
                const matchedLogics = sortedLogics
                    .filter((l) => l.action !== LogicAction.ANSWERS_HIDE)
                    .filter((logic) =>
                        logicMatch(
                            logic,
                            groupsActors.map((g) => g.getSnapshot().context),
                            answersActors.map((a) => a.getSnapshot().context),
                            responseActor.getSnapshot().context,
                            getQ(question),
                            quotaByQuestionId,
                        ),
                    )
                    .concat(matchedGlobalLogic.filter((l) => l.action !== LogicAction.ANSWERS_HIDE))
                    .concat(
                        matchedExtendedLogicWithBundleAndQuestionIndex.filter(
                            (l) => l.action !== LogicAction.ANSWERS_HIDE,
                        ),
                    );
                const matchedPagesLogics = matchedLogics.filter(
                    (l) => l.transition === LogicTransition.PAGES && l.action !== LogicAction.HIDE,
                );
                const matchedQuestionsLogics = matchedLogics.filter(
                    (l) => l.transition === LogicTransition.QUESTIONS && l.action !== LogicAction.HIDE,
                );
                const hideMatchedPagesLogics = matchedLogics.filter(
                    (l) => l.transition === LogicTransition.PAGES && l.action === LogicAction.HIDE,
                );
                const hideMatchedQuestionsLogics = matchedLogics.filter(
                    (l) => l.transition === LogicTransition.QUESTIONS && l.action === LogicAction.HIDE,
                );
                matchedHideAnswersLogics.forEach((logic) => {
                    if (!hiddenAnswers[logic.toQuestion]) {
                        hiddenAnswers[logic.toQuestion] = [];
                    }
                    if (Array.isArray(logic.answersToHide)) {
                        hiddenAnswers[logic.toQuestion] = [...hiddenAnswers[logic.toQuestion], ...logic.answersToHide];
                    }
                });

                if (
                    matchedPagesLogics.length ||
                    matchedQuestionsLogics.length ||
                    hideMatchedPagesLogics.length ||
                    hideMatchedQuestionsLogics.length
                ) {
                    if (matchedPagesLogics.length || matchedQuestionsLogics.length) {
                        execLogic = null;
                        if (matchedPagesLogics.length) {
                            const lastPagesLogic = JSON.parse(
                                JSON.stringify(matchedPagesLogics[matchedPagesLogics.length - 1]),
                            );
                            const commonToPages = matchedPagesLogics
                                .slice(0, -1)
                                .reduce((acc, item) => acc.concat(item.toPages), []);
                            commonToPages.map((el) => lastPagesLogic.toPages.push(el));
                            pagesLogicMatches.push(lastPagesLogic);
                        }
                        if (matchedQuestionsLogics.length) {
                            const lastQuestionsLogic = JSON.parse(
                                JSON.stringify(matchedQuestionsLogics[matchedQuestionsLogics.length - 1]),
                            );
                            const commonToQuestions = matchedQuestionsLogics
                                .slice(0, -1)
                                .reduce((acc, item) => acc.concat(item.toQuestions), []);
                            commonToQuestions.map((el) => lastQuestionsLogic.toQuestions.push(el));
                            questionsLogicMatches.push(lastQuestionsLogic);
                        }
                    }
                    if (hideMatchedPagesLogics.length || hideMatchedQuestionsLogics.length) {
                        execLogic = null;
                        if (hideMatchedPagesLogics.length) {
                            const lastPagesLogic = JSON.parse(
                                JSON.stringify(hideMatchedPagesLogics[hideMatchedPagesLogics.length - 1]),
                            );
                            const commonToPages = hideMatchedPagesLogics
                                .slice(0, -1)
                                .reduce((acc, item) => acc.concat(item.toPages), []);
                            commonToPages.map((el) => lastPagesLogic.toPages.push(el));
                            hidePagesLogicMatches.push(lastPagesLogic);
                        }
                        if (hideMatchedQuestionsLogics.length) {
                            const lastQuestionsLogic = JSON.parse(
                                JSON.stringify(hideMatchedQuestionsLogics[hideMatchedQuestionsLogics.length - 1]),
                            );
                            const commonToQuestions = hideMatchedQuestionsLogics
                                .slice(0, -1)
                                .reduce((acc, item) => acc.concat(item.toQuestions), []);
                            commonToQuestions.map((el) => lastQuestionsLogic.toQuestions.push(el));
                            hideQuestionsLogicMatches.push(lastQuestionsLogic);
                        }
                    }
                } else {
                    for (const logic of sortedLogics) {
                        /*
                        TODO disabled, take last to satisfy compliance with legacy system
                        if (execLogic) break; // already found
                        */

                        if (
                            logicMatch(
                                logic,
                                groupsActors.map((g) => g.getSnapshot().context),
                                answersActors.map((a) => a.getSnapshot().context),
                                responseActor.getSnapshot().context,
                                getQ(question),
                                quotaByQuestionId,
                            )
                        ) {
                            execLogic = logic;
                        }
                    }
                }
            }

            questions.push(question);

            return questions;
        }, [] as Array<QuestionActorRef>);

        if (bundle.questions.length > 0) {
            acc.push(bundle);
        }

        return acc;
    }, [] as Array<BundleEntity>);
    adjustQuestions(newBundles, true);

    if (
        hidePagesLogicMatches.length > 0 ||
        hideQuestionsLogicMatches.length > 0 ||
        questionsLogicMatches.length > 0 ||
        pagesLogicMatches.length > 0
    ) {
        const allMatches = [
            ...hidePagesLogicMatches,
            ...hideQuestionsLogicMatches,
            ...pagesLogicMatches,
            ...questionsLogicMatches,
        ];

        execLogic = allMatches[allMatches.length - 1];
    }
    let lastBundle = execLogic && createLastBundle(execLogic);
    if ((!lastBundle && questionsLogicMatches.length > 0) || pagesLogicMatches.length > 0) {
        const lastMatch =
            pagesLogicMatches[pagesLogicMatches.length - 1] || questionsLogicMatches[questionsLogicMatches.length - 1];
        lastBundle = lastMatch && createLastBundle(lastMatch);
    }

    if (lastBundle) {
        newBundles.push(lastBundle);
    }

    return { newBundles, hiddenAnswers };
};

export const excludeHiddenAnswers = (answersActors: Array<QuestionAnswerActor>) =>
    answersActors.filter((a) => {
        return a.getSnapshot().context.hidden !== true && a.getSnapshot().context.hiddenByAnswersHide !== true;
    });
